/**
 * features/finance/batchWrite.ts
 * ------------------------------------------------------------------
 * Writing payment runs.
 *
 * THE BATCH DOES NOT PAY ANYTHING. Confirming a run calls the SAME
 * `payVoucher` Cloud Function that pays a single voucher, once per
 * voucher, so every control the voucher carries still runs: the
 * distinct-actor rule, and — the one that matters here — the comparison
 * of the banking authorised at approval against the banking about to be
 * paid. A bulk path that wrote `status: paid` directly would be a way to
 * bypass those ninety at a time, which is precisely the shape of the
 * fraud the snapshots exist to catch.
 *
 * THAT MEANS CONFIRMATION CAN PARTLY FAIL, and it says so rather than
 * pretending. A voucher whose destination changed since approval is
 * refused by the server; the rest of the run still pays. Reporting the
 * refusals by name is the whole point — a silent "done" over ninety
 * payments with three missing is worse than an error.
 *
 * THE NUMBER IS ALLOCATED INSIDE THE TRANSACTION, so a failed create
 * cannot burn one.
 * ------------------------------------------------------------------ */
import { collection, doc, runTransaction } from 'firebase/firestore';
import { db, updateDoc } from '@/lib/firebase';
import {
  BATCH_COLLECTION, batchCounterId, formatBatchNo, batchTotal, batchActionProblem,
  type PaymentBatch,
} from '@/lib/paymentBatches';
import { payVoucher } from '@/features/finance/voucherWrite';
import type { Voucher } from '@/lib/vouchers';

const COUNTERS = 'financeCounters';
const now = () => new Date().toISOString();

export interface NewBatchInput {
  valueDate: string;
  bankFormatId: string;
  vouchers: Voucher[];
  actor: { uid: string; name?: string };
}

export async function createBatch(input: NewBatchInput): Promise<PaymentBatch> {
  if (input.vouchers.length === 0) {
    throw new Error('Choose at least one approved voucher for the run.');
  }
  const year = input.valueDate.slice(0, 4);
  const at = now();

  return runTransaction(db, async (tx) => {
    const counterRef = doc(db, COUNTERS, batchCounterId(year));
    const snap = await tx.get(counterRef);
    const seq = ((snap.exists() ? Number(snap.data().seq) : 0) || 0) + 1;
    tx.set(counterRef, { seq, updatedAt: at }, { merge: true });

    const ref = doc(collection(db, BATCH_COLLECTION));
    const batch: PaymentBatch = {
      id: ref.id,
      batchNo: formatBatchNo(seq, year),
      fy: `FY${year}`,
      status: 'draft',
      voucherIds: input.vouchers.map((v) => v.id),
      valueDate: input.valueDate,
      bankFormatId: input.bankFormatId,
      total: batchTotal(input.vouchers),
      count: input.vouchers.length,
      createdBy: input.actor.uid,
      ...(input.actor.name ? { createdByName: input.actor.name } : {}),
      createdAt: at,
      updatedAt: at,
    };
    tx.set(ref, batch);
    return batch;
  });
}

/** Change what is in a draft run. Only a draft — past that a file exists. */
export async function setBatchVouchers(
  batch: PaymentBatch, vouchers: Voucher[],
): Promise<void> {
  if (batch.status !== 'draft') {
    throw new Error('This run has already gone to the bank; its contents cannot change.');
  }
  await updateDoc(doc(db, BATCH_COLLECTION, batch.id), {
    voucherIds: vouchers.map((v) => v.id),
    total: batchTotal(vouchers),
    count: vouchers.length,
    updatedAt: now(),
  });
}

/** Record that the file has been produced and handed to the bank. */
export async function markBatchExported(
  batch: PaymentBatch, actor: { uid: string; name?: string },
): Promise<void> {
  const problem = batchActionProblem(batch, 'export');
  if (problem) throw new Error(problem);

  const at = now();
  await updateDoc(doc(db, BATCH_COLLECTION, batch.id), {
    status: 'exported',
    exportedAt: at,
    exportedBy: actor.uid,
    ...(actor.name ? { exportedByName: actor.name } : {}),
    updatedAt: at,
  });
}

export interface ConfirmResult {
  paid: string[];
  /** Voucher number -> why the server refused it. */
  refused: { voucherNo: string; reason: string }[];
}

/**
 * The bank has confirmed the run. Pay each voucher through the server.
 *
 * Sequential, not parallel: each call re-reads the voucher and the
 * payee's current banking, and firing ninety at once at a function
 * capped to three instances buys nothing but timeouts.
 */
export async function confirmBatch(
  batch: PaymentBatch,
  vouchers: Voucher[],
  bankRef: string,
  actor: { uid: string; name?: string },
): Promise<ConfirmResult> {
  const problem = batchActionProblem(batch, 'confirm');
  if (problem) throw new Error(problem);

  const result: ConfirmResult = { paid: [], refused: [] };

  for (const v of vouchers) {
    if (v.currentStatus === 'paid') { result.paid.push(v.voucherNo); continue; }
    try {
      await payVoucher(v.id, bankRef, `Paid in run ${batch.batchNo}`);
      result.paid.push(v.voucherNo);
    } catch (e) {
      result.refused.push({ voucherNo: v.voucherNo, reason: (e as Error).message });
    }
  }

  /* The run is confirmed only if everything in it actually paid. A run
     marked confirmed with three vouchers still outstanding would drop
     them out of the creditors report while remaining unpaid. */
  if (result.refused.length === 0) {
    const at = now();
    await updateDoc(doc(db, BATCH_COLLECTION, batch.id), {
      status: 'confirmed',
      confirmedAt: at,
      confirmedBy: actor.uid,
      ...(actor.name ? { confirmedByName: actor.name } : {}),
      ...(bankRef ? { bankRef } : {}),
      updatedAt: at,
    });
  }

  return result;
}

export async function cancelBatch(batch: PaymentBatch, reason: string): Promise<void> {
  const problem = batchActionProblem(batch, 'cancel');
  if (problem) throw new Error(problem);
  if (!reason.trim()) throw new Error('Say why the run is being cancelled.');

  const at = now();
  await updateDoc(doc(db, BATCH_COLLECTION, batch.id), {
    status: 'cancelled',
    cancelledAt: at,
    cancelledReason: reason.trim(),
    updatedAt: at,
  });
}
